import { inject, Injectable } from '@angular/core';
import { StorageKeys } from '@core/utils/storage-keys.util';
import { UserService } from '../user/user.service';
import Pusher, { type Channel } from 'pusher-js';
import { Subject } from 'rxjs';
import { SettingsService } from '@core/config/settings.service';

export enum PUSHER_CHANNEL_KEY {
  Notifications = 'groups.notifications',
  Deployments = 'groups.deployments',
  GroupsRides = 'private-groups.carnival',
  System = 'presence-channel-system',
}

export enum PUSHER_EVENT {
  NotificationChanged = 'notification.changed',
  DeploymentNew = 'deployments.new',
  RideChanged = 'ride.changed',
}

type ChannelEventMap = {
  [PUSHER_CHANNEL_KEY.Notifications]: PUSHER_EVENT.NotificationChanged;
  [PUSHER_CHANNEL_KEY.Deployments]: PUSHER_EVENT.DeploymentNew;
  [PUSHER_CHANNEL_KEY.GroupsRides]: PUSHER_EVENT.RideChanged;
};

export const PUSHER_CHANNELS: PUSHER_CHANNEL_KEY[] = [
  PUSHER_CHANNEL_KEY.Notifications,
  PUSHER_CHANNEL_KEY.Deployments,
  PUSHER_CHANNEL_KEY.GroupsRides,
];

@Injectable({
  providedIn: 'root',
})
export class PusherService {
  private readonly settings = inject(SettingsService);
  private readonly userService = inject(UserService);
  private pusher?: Pusher;
  private channels?: Record<PUSHER_CHANNEL_KEY, Channel>;

  initPusher(): void {
    try {
      const user = this.userService.user();
      if (!user) return;
      if (this.pusher) return;

      const accessToken = localStorage.getItem(StorageKeys.ATO);
      if (!accessToken) return;

      this.pusher = new Pusher(this.settings.pusher.key, {
        cluster: this.settings.pusher.cluster,
        enableStats: true,
        authEndpoint: `${this.settings.apiBaseUrl}/pusher/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      });

      this.channels = {
        [PUSHER_CHANNEL_KEY.Notifications]: this.pusher.subscribe(PUSHER_CHANNEL_KEY.Notifications),
        [PUSHER_CHANNEL_KEY.Deployments]: this.pusher.subscribe(PUSHER_CHANNEL_KEY.Deployments),
        [PUSHER_CHANNEL_KEY.GroupsRides]: this.pusher.subscribe(PUSHER_CHANNEL_KEY.GroupsRides),
        [PUSHER_CHANNEL_KEY.System]: this.pusher.subscribe(PUSHER_CHANNEL_KEY.System),
      };
    } catch (error) {
      console.error('Error initializing Pusher:', error);
    }
  }

  subscribeEvent<T extends keyof ChannelEventMap>(
    channelName: T,
    eventName: ChannelEventMap[T]
  ): Promise<Subject<unknown>> {
    if (!this.channels) {
      this.initPusher();
    }

    return new Promise(resolve => {
      const eventSubject = new Subject<unknown>();

      if (this.channels && this.channels[channelName]) {
        this.channels[channelName].bind(eventName, (data: unknown) => {
          eventSubject.next(data);
        });
      }

      resolve(eventSubject);
    });
  }

  unsubscribeChannel(channelName: PUSHER_CHANNEL_KEY): void {
    if (this.pusher) {
      this.pusher.unsubscribe(channelName);
    }
  }

  disconnect(): void {
    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = undefined;
      this.channels = undefined;
    }
  }
}
